import type { ComponentDocMap } from './types'

/**
 * Documentation for dataset instruction (DSI) fields.
 * Cross-component. Keep in sync with DatasetInstruction and DatasetInstructionFilter
 * in src/api/dataset/index.d.ts
 */
export const dsiDocs: ComponentDocMap = {
  type: {
    description:
      'Identifies the backend handler. "template" is resolved from base_config at parse time. "calc" is browser-side only.',
    type: '"template" | "calc" | "dbs" | "optresults" | "pps" | "property_values" | "dt_property_values" | "custom/*"',
    example: '"dbs"',
  },
  filter: {
    description:
      'Optional object that controls time windowing and value transformations applied to the fetched dataset before it is passed to the component.',
    type: 'DatasetInstructionFilter',
    example: '{ "start_time": "$start_time", "end_time": "$end_time", "aggregate": "sum" }',
  },
  contract: {
    description:
      'Type-specific configuration for the DSI. Shape depends on the "type" field: DatasetContractCalc for calc, DatasetContractDbS for dbs, DatasetContractOptResults for optresults/property_values, etc.',
    type: 'DatasetContractCalc | DatasetContractDbS | DatasetContractOptResults | ...',
    example: '{ "metadata_filter": { "include": { "system": ["$system"] } } }',
  },
  return_id: {
    description:
      'The key assigned to the returned dataset. Must match data_id on the corresponding props.items entry for data to appear in the rendered component.',
    type: 'string',
    example: '"heatload_forecast"',
  },
  'filter.aggregate': {
    description:
      'Server-side aggregation applied before returning data. Use aggregate_in_frontend: true for browser aggregation instead.',
    type: '"latest" | "mean" | "min" | "max" | "sum" | ""',
    example: '"sum"',
  },
  'filter.aggregate_in_frontend': {
    description:
      'Performs the aggregation in the browser rather than on the server. Required when using calc-derived datasets as inputs.',
    type: 'boolean',
    example: 'true',
  },
  'filter.start_time': {
    description: 'Start of the requested time window. Defaults to the $start_time alias (168h in the past).',
    type: 'ISODateTime | "$start_time" | alias',
    example: '"$start_time"',
  },
  'filter.end_time': {
    description: 'End of the requested time window. Defaults to the $end_time alias (168h in the future).',
    type: 'ISODateTime | "$end_time" | alias',
    example: '"$end_time"',
  },
  'filter.offset_start_time': {
    description: 'Hour offset added to start_time. Negative = earlier, positive = later.',
    type: 'number',
    example: '-168',
  },
  'filter.offset_end_time': {
    description: 'Hour offset added to end_time.',
    type: 'number',
    example: '168',
  },
  'filter.zero_is_nan': {
    description: 'Treats zero values as missing/null.',
    type: 'boolean',
    example: 'true',
  },
  'filter.limit_min': {
    description: 'Clamps all values to a minimum. Use 0 to suppress negative values (e.g. for cost DSIs).',
    type: 'number',
    example: '0',
  },
  'filter.limit_max': {
    description: 'Clamps all values to a maximum.',
    type: 'number',
    example: '100',
  },
  'filter.absolute_values': {
    description: 'Makes all values positive.',
    type: 'boolean',
    example: 'true',
  },
  'filter.negate_values': {
    description: 'Flips the sign of all values.',
    type: 'boolean',
    example: 'true',
  },
  'filter.aggregate_duration': {
    description:
      'Bucket size for aggregation. Must be set when filter.aggregate is also set. Use ISO-8601 duration strings.',
    type: 'string',
    example: '"1h"',
  },
  'filter.interpolate_method': {
    description: 'How gaps between data points are filled before aggregation or display.',
    type: '"linear" | "forwardFill" | "backwardFill" | "zero" | "null"',
    example: '"forwardFill"',
  },
  'filter.offset_unit': {
    description: 'Unit for offset_start_time and offset_end_time. Currently only "h" (hours) is supported.',
    type: '"h"',
    example: '"h"',
  },
  'filter.show_as_negative': {
    description: 'Makes all values negative (even values that are already negative become more negative).',
    type: 'boolean',
    example: 'true',
  },
  'filter.skip_previous_zeros': {
    description:
      'Ignores leading zero values at the start of the series (useful for cumulative series that start at 0).',
    type: 'boolean',
    example: 'true',
  },
  'filter.fill_period_with_first_index_value': {
    description: 'Fills the entire requested time period with the first value found in the series.',
    type: 'boolean',
    example: 'true',
  },
  'filter.add_one_zero_value_to_end_of_dataset': {
    description: 'Appends a 0 at the end of the time series. Useful for bar charts that need a closing zero.',
    type: 'boolean',
    example: 'true',
  },
  'filter.mark_hours_ahead': {
    description: 'Marks timestamps in the series that are in the future relative to the current time.',
    type: 'boolean',
    example: 'true',
  },
  'filter.created_at': {
    description: 'Filters to data that was created at a specific timestamp (used for snapshot queries).',
    type: 'ISODateTime | string',
    example: '"$opt_time"',
  },
  'filter.created_at_operator': {
    description: 'Comparison operator used together with filter.created_at.',
    type: '"eq" | "lt" | "lte" | "gt" | "gte"',
    example: '"lte"',
  },
  'filter.relative_created_at': {
    description:
      'Offset relative to now for the created_at filter, expressed as an ISO-8601 duration (e.g. "-PT1H" = 1 hour ago).',
    type: 'string',
    example: '"-PT1H"',
  },
  'filter.ignore_dataset_times': {
    description:
      'Ignores the time index on the returned dataset and uses the filter time window directly. Useful for property-style data that carries no meaningful timestamps.',
    type: 'boolean',
    example: 'true',
  },
  'contract.operator': {
    description: 'Arithmetic or logical operator for calc DSIs.',
    type: '"-" | "+" | "/" | "*" | "max" | "min" | "if" | "ifnot"',
    example: '"+"',
  },
  'contract.variables': {
    description: 'Input return_ids (or literal numbers) for a calc DSI. Must reference return_ids of earlier DSIs.',
    type: '(string | number)[]',
    example: '["optimal.total_cost", "outcome.total_cost"]',
  },
  'contract.element_wise': {
    description:
      'For calc DSIs: applies the operator element-by-element across the time series rather than aggregating to a scalar.',
    type: 'boolean',
    example: 'true',
  },
  'contract.null_as_zero': {
    description: 'For calc DSIs: treats null values as 0 when evaluating. Default is true.',
    type: 'boolean',
    example: 'false',
  },
  'contract.metadata_filter.include': {
    description:
      'For dbs DSIs: key-value filter specifying which timeseries to include. Each value is a string or array of strings.',
    type: 'Record<string, string | string[]>',
    example:
      '{ "system": ["$system"], "abs_service": ["abs_optservice"], "optjob_type": ["$optjob_type"], "internal_id": ["total_cost"] }',
  },
  'contract.id_renaming': {
    description:
      'For dbs DSIs: renames return_ids of fetched series to avoid collisions. Original name is the key, new name is the value.',
    type: 'Record<string, string>',
    example: '{ "total_cost": "optimal.total_cost" }',
  },
  'contract.opt_job_type_id': {
    description:
      'For optresults DSIs: the optimization job type to read from. Use the $opt_job_type_id alias or a literal from OPTJOB_TYPE_ID_TO_NAME.',
    type: 'number | string',
    example: '"$opt_job_type_id"',
  },
  'contract.sources': {
    description: 'For optresults/dt_property_values DSIs: list of source signals to fetch, each with a return_id.',
    type: '{ source: string; return_id: string }[]',
    example: '[{ source: "$demand_heatload", return_id: "heatload" }]',
  },
  'contract.sources[].source': {
    description: 'Backend source identifier/path to fetch (for example production node signal name).',
    type: 'string',
    example: '"production.heat.main.a1_tk"',
  },
  'contract.sources[].return_id': {
    description: 'Dataset key assigned to this source. Referenced from props.items[].data_id.',
    type: 'string',
    example: '"production.heat.main.a1_tk"',
  },
  'contract.accumulate': {
    description:
      'For calc DSIs: accumulates values over time. "sum" gives a running total, "mean" gives a running average.',
    type: '"" | "sum" | "mean"',
    example: '"sum"',
  },
  'contract.positive_total': {
    description: 'For calc DSIs: clamps the total result to be non-negative.',
    type: 'boolean',
    example: 'true',
  },
  'contract.positive_parts': {
    description: 'For calc DSIs: clamps each individual variable to be non-negative before combining.',
    type: 'boolean',
    example: 'true',
  },
  'contract.steps': {
    description:
      'For calc DSIs: defines a multi-step calculation. Each step is a partial CalcContract with its own return_id, allowing intermediate results to be referenced as variables in later steps.',
    type: 'DatasetContractCalcStep[]',
    example: '[{ "return_id": "intermediate", "variables": ["a", "b"], "operator": "+" }]',
  },
  'contract.if_nan': {
    description: 'For calc DSIs: substitutes this number when the result of the calculation is NaN.',
    type: 'number',
    example: '0',
  },
  'contract.opt_model_id': {
    description: 'For optresults DSIs: the optimization model to read results from.',
    type: 'number | string',
    example: '"$opt_model_id"',
  },
  'contract.opt_time': {
    description: 'For optresults DSIs: restricts to a specific optimization run by its start time.',
    type: 'ISODateTime | string',
    example: '"$opt_time"',
  },
  'contract.system_id': {
    description: 'For optresults/pps/dt_property_values DSIs: the system whose data to fetch.',
    type: 'number | string',
    example: '"$system"',
  },
  'contract.subtype': {
    description: 'For optresults DSIs: further narrows the result set to a specific result subtype.',
    type: 'string | null',
    example: '"unit_commitment"',
  },
  'contract.priority_levels': {
    description: 'For pps/dt_property_values DSIs: which priority levels to include in the response.',
    type: 'number[]',
    example: '[1, 2]',
  },
  'contract.use_default_values': {
    description: 'For pps/dt_property_values DSIs: fall back to default property values when no override is set.',
    type: 'boolean',
    example: 'true',
  },
  'contract.elplan_info_id': {
    description: 'For elplan_bid_management DSIs: which elplan info record to read bid data from.',
    type: 'number | string',
    example: '"$elplan_info_id"',
  },
  'contract.partition_id': {
    description:
      'For elplan_bid_management DSIs: returns only bids in this elplan partition. Omit it to return every partition.',
    type: 'number | string',
    example: '"$elplan_partition_id"',
  },
  'contract.global_read_api': {
    description: 'For dbs DSIs: reads from the global read API instead of the system-scoped one.',
    type: 'boolean',
    example: 'true',
  },
  'contract.dataset_ids': {
    description:
      'For dbs DSIs: fetches only the specific timeseries with these numeric IDs, bypassing metadata_filter.',
    type: 'number[]',
    example: '[12345, 67890]',
  },
  'contract.use_dbs_ids_as_return_ids': {
    description: 'For dbs DSIs: uses each timeseries numeric ID as its return_id instead of its metadata-derived name.',
    type: 'boolean',
    example: 'true',
  },
  'contract.decimals': {
    description: 'For dbs DSIs: number of decimal places to request from the database when fetching values.',
    type: 'number',
    example: '2',
  },
}
